import { Buttons, Inputs } from "../../../components"
import { useQuery } from "../../../hooks"
import { isEmpty, omit } from "lodash"
import moment from "moment"
import { useEffect, useState } from "react"
import Popup from "reactjs-popup"
import { useTranslation } from 'react-i18next'
import "./styles.scss"

const DatepickerComponent = (props) => {
    const [isRendered, setIsRendered] = useState(false)
    useEffect(() => { if (!isRendered) setIsRendered(true) }, [isRendered])
    return <Inputs.Datepicker {...props} disabled={!isRendered} />
}

const DBDropdownComponent = ({ allowFilter, single, setQuery, filter, keyProp: key, query, setFilter, values }) => {
    const { t } = useTranslation()
    // TODO handlePagination
    const [options, setOptions] = useState([])
    useEffect(() => {
        if (allowFilter?.type === 'dbDropdown' && isEmpty(options)) allowFilter.fetch()
            .then(({ data: { payload } }) => setOptions(allowFilter.transform(payload)))
            .catch((error) => console.error(error))
    }, [allowFilter, options])
    return single ? <>
        <Inputs.Text value={query} onChange={({ target: { value } }) => setQuery(value)} />
        <Inputs.RadioButtons
            value={filter[key]}
            onChange={(value) => isEmpty(value) ? setFilter(omit(filter, key)) : setFilter({ ...filter, [key]: value })}
            options={(values || options)?.map(({ _id, name }) => ({ value: _id, label: t(name) }))?.filter(({ label }) => !query || label?.includes(query))}
            col
        />
    </> : <>
        <Inputs.Text value={query} onChange={({ target: { value } }) => setQuery(value)} />
        <Inputs.Checkboxes
            value={filter[key]}
            onChange={(value) => isEmpty(value) ? setFilter(omit(filter, key)) : setFilter({ ...filter, [key]: value })}
            options={(values || options)?.map(({ _id, name }) => ({ value: _id, label: t(name) }))?.filter(({ label }) => !query || label?.includes(query))}
            col
        />
    </>
}

const SortingComponent = ({
    columnRaw,
    filterKey: key,
    sortKey,
    allowSort,
    allowFilter,
    useColumnsManager,
    instanceId
}) => {
    const { t } = useTranslation()
    const sortButtons = [{ label: t('shared.ascending'), value: '1' }, { label: t('shared.descending'), value: '-1' }]

    const { sortBy: sortParam = '{}', filter: filterParam = '{}', handleUrlChange } = useQuery()
    const [sortBy, setSortBy] = useState(JSON.parse(sortParam)[sortKey])
    const [filter, setFilter] = useState(JSON.parse(filterParam))
    const [query, setQuery] = useState('')

    const handleApply = () => {
        if (!isEmpty(filter)) handleUrlChange({ filter: JSON.stringify(filter) })
        else handleUrlChange({ filter: null })
        if (!isEmpty(sortBy)) handleUrlChange({ sortBy: JSON.stringify({ [sortKey]: sortBy }) })
        else if (isEmpty(omit(JSON.parse(sortParam), sortKey))) handleUrlChange({ sortBy: null })
    }

    const renderFilter = () => {
        const { type, single, values } = allowFilter || {}
        const { startDate, endDate } = filter[key] || {}
        switch (type) {
            case 'text':
                return <Inputs.Text
                    placeholder={t('shared.search')}
                    value={filter[key] || ''}
                    onChange={({ target: { value } }) => setFilter({ ...filter, [key]: value })}
                />
            case 'dropdown':
                return single ? <>
                    <Inputs.Text value={query} onChange={({ target: { value } }) => setQuery(value)} />
                    <Inputs.RadioButtons
                        value={filter[key]}
                        onChange={(value) => isEmpty(value) ? setFilter(omit(filter, key)) : setFilter({ ...filter, [key]: value })}
                        options={values?.filter((option) => !query || option?.includes(query))}
                        col
                    />
                </> : <>
                    <Inputs.Text value={query} onChange={({ target: { value } }) => setQuery(value)} />
                    <Inputs.Checkboxes
                        value={filter[key]}
                        onChange={(value) => isEmpty(value) ? setFilter(omit(filter, key)) : setFilter({ ...filter, [key]: value })}
                        options={values?.filter((option) => !query || option?.includes(query))}
                        col
                    />
                </>
            case 'dbDropdown':
            case 'idDropdown':
                return <DBDropdownComponent {...{ allowFilter, single, setQuery, filter, keyProp: key, query, setFilter, values }} />
            case 'date':
                return <div className="row" style={{ width: 280 }} id="shared-sorting-component-date">
                    <div className="col">
                        <span>{t('shared.from')}</span>
                        <DatepickerComponent
                            disableMargin
                            value={(allowFilter.datesRoot
                                ? filter?.[allowFilter.datesRoot[0]] ? moment(filter[allowFilter.datesRoot[0]]).toDate() : null
                                : startDate ? moment(startDate).toDate() : null)}
                            onChange={(value) => allowFilter.datesRoot
                                ? setFilter({ ...filter, [allowFilter.datesRoot[0]]: value === '' ? undefined : moment(value).startOf('day').toDate() })
                                : setFilter({ ...filter, [key]: { ...(filter?.[key]), startDate: value === '' ? undefined : moment(value).startOf('day').toDate() } })}
                            portalId='shared-sorting-component-date'
                        />
                    </div>
                    <div style={{ width: 15 }} />
                    <div className="col">
                        <span>{t('shared.to')}</span>
                        <DatepickerComponent
                            disableMargin
                            value={(allowFilter.datesRoot
                                ? filter?.[allowFilter.datesRoot[1]] ? moment(filter[allowFilter.datesRoot[1]]).toDate() : null
                                : endDate ? moment(endDate).toDate() : null)}
                            onChange={(value) => allowFilter.datesRoot
                                ? setFilter({ ...filter, [allowFilter.datesRoot[1]]: value === '' ? undefined : moment(value).endOf('day').toDate() })
                                : setFilter({ ...filter, [key]: { ...(filter?.[key]), endDate: value === '' ? undefined : moment(value).endOf('day').toDate() } })}
                            portalId='shared-sorting-component-date'
                        />
                    </div>
                </div>
            case 'numberRange':
                return <div className="row" style={{ width: 230 }}>
                    <div className="col">
                        <span>{t('shared.from')}</span>
                        <Inputs.Text
                            disableMargin
                            value={filter?.[key]?.min ?? ''}
                            onChange={({ target: { value } }) => setFilter({ ...filter, [key]: { ...(filter?.[key]), min: value === '' ? undefined : value } })}
                        />
                    </div>
                    <div style={{ width: 15 }} />
                    <div className="col">
                        <span>{t('shared.to')}</span>
                        <Inputs.Text
                            disableMargin
                            value={filter?.[key]?.max ?? ''}
                            onChange={({ target: { value } }) => setFilter({ ...filter, [key]: { ...(filter?.[key]), max: value === '' ? undefined : value } })}
                        />
                    </div>
                </div>
            case 'boolean':
                return <Inputs.RadioButtons
                    options={[t('shared.yes'), t('shared.no')]}
                    value={filter[key] === false ? t('shared.no') : filter[key] === true ? t('shared.yes') : null}
                    onChange={(value) => setFilter({ ...filter, [key]: value === t('shared.yes') })}
                />
            default:
                return null
        }
    }

    return <Popup
        trigger={<div className="icon icon-arrow-down" />}
        className="popup-shared-sorting-component"
        position="bottom"
        keepTooltipInside={`.table-container-${instanceId}`}
    >
        {(close) => <div className="shared-sorting-component-container col">
            {useColumnsManager && <div className="columns-manager">
                <h3>{t('shared.functions')}</h3>
                {useColumnsManager.lockedColumns?.some(({ value }) => value === key)
                    ? <p onClick={() => { useColumnsManager.setLockedColumns((lockedColumns) => lockedColumns.filter(({ value }) => value !== key)); close() }}>{t('shared.unfreezeColumn')}</p>
                    : <p onClick={() => { useColumnsManager.setLockedColumns((lockedColumns) => [...lockedColumns, columnRaw]); close() }}>{t('shared.freezeColumn')}</p>
                }
            </div>}
            {allowSort && (
                <div className="sortBy">
                    <h3>{t('shared.sortBy')}</h3>
                    <Inputs.RadioButtons col options={sortButtons} onChange={(data) => setSortBy(data)} value={sortBy} />
                </div>
            )}
            {allowFilter && (
                <div className="filter">
                    <h3>{t('shared.filterBy')}</h3>
                    {renderFilter()}
                </div>
            )}
            {(allowSort || allowFilter) && (
                <div className="buttons row">
                    <p
                        className="btn-clear"
                        onClick={() => {
                            setFilter(omit(filter, allowFilter?.datesRoot ?? key))
                            setSortBy('')
                            setQuery('')
                        }}
                    >
                        {t('shared.clear')}
                    </p>
                    <Buttons.Raised
                        text={t('shared.save')}
                        onClick={() => {
                            handleApply()
                            close()
                        }}
                    />
                </div>
            )}
        </div>}
    </Popup>
}

export default SortingComponent